import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth, defaultPerms } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Shield, Check, ShieldCheck, BadgeCheck } from "lucide-react";

const roleStyles = {
  admin: "bg-zinc-900 text-white border-zinc-900",
  manager: "bg-[#002FA7] text-white border-[#002FA7]",
  staff: "bg-zinc-100 text-zinc-900 border-zinc-300",
  viewer: "bg-white text-zinc-700 border-zinc-300",
};

const roleDescriptions = {
  admin: "Default: full access. Manages team and deletes records.",
  manager: "Default: manages clients, invoices, returns, tasks. Can delete.",
  staff: "Default: creates and edits records. Cannot delete or manage team.",
  viewer: "Default: read-only access plus AI assistant.",
};

const PERM_GROUPS = [
  { title: "View access", keys: [
    { key: "dashboard_view",  label: "Dashboard & stats" },
    { key: "clients_view",    label: "Client list" },
    { key: "invoices_view",   label: "Invoice list & totals" },
    { key: "returns_view",    label: "Tax returns list" },
    { key: "tasks_view",      label: "Tasks list" },
    { key: "expenses_view",   label: "Expenses list" },
    { key: "banks_view",      label: "Bank accounts" },
    { key: "cash_view",       label: "Cash & Books summary" },
  ]},
  { title: "Edit / Create", keys: [
    { key: "clients_write",   label: "Create / edit clients" },
    { key: "invoices_write",  label: "Create / edit invoices" },
    { key: "returns_write",   label: "Create / edit tax returns" },
    { key: "tasks_write",     label: "Create / edit tasks" },
    { key: "expenses_write",  label: "Create / edit expenses" },
    { key: "delete",          label: "Delete records" },
  ]},
  { title: "Admin & Tools", keys: [
    { key: "team_manage",     label: "Manage team & roles" },
    { key: "ai",              label: "Use AI assistant" },
  ]},
];

const PERM_LABELS = PERM_GROUPS.flatMap(g => g.keys);

const empty = { name: "", email: "", password: "", role: "staff" };

export default function Team() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [perms, setPerms] = useState(defaultPerms("staff"));
  const [pending, setPending] = useState([]);
  const isFounding = !!current?.subscription_status?.founding;

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
    if (isFounding) {
      try {
        const { data: pendingData } = await api.get("/admin/pending-approvals");
        setPending(pendingData || []);
      } catch (e) { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const approve = async (userId, grantAdmin) => {
    try {
      await api.post(`/admin/users/${userId}/approve`, { grant_admin: grantAdmin });
      toast.success(grantAdmin ? "User approved with admin rights" : "User approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    }
  };

  if (current?.role !== "admin") {
    return (
      <div className="p-8">
        <div className="bg-white border border-zinc-200 p-10 text-center max-w-md mx-auto">
          <Shield size={32} className="mx-auto mb-4 text-zinc-400" />
          <h2 className="font-display font-bold text-xl mb-2">Admin only</h2>
          <p className="text-zinc-600 text-sm">Team management is restricted to administrators.</p>
        </div>
      </div>
    );
  }

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setPerms(defaultPerms("staff"));
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setPerms({ ...defaultPerms(u.role), ...(u.permissions || {}) });
    setOpen(true);
  };

  const onRoleChange = (newRole) => {
    setForm({ ...form, role: newRole });
    setPerms(defaultPerms(newRole));  // reset to that role's defaults
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, permissions: perms };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Member updated");
      } else {
        await api.post("/users", { ...form, permissions: perms });
        toast.success("Member added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const PermBadge = ({ on }) => (
    <span className={`inline-flex items-center justify-center w-5 h-5 ${on ? "bg-[#039855] text-white" : "bg-zinc-100 text-zinc-400"}`}>
      {on ? <Check size={12} strokeWidth={3} /> : "—"}
    </span>
  );

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">People</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">Team & Permissions</h1>
          <p className="text-zinc-600 mt-2">Control exactly what each member can do.</p>
        </div>
        <button onClick={openNew} data-testid="new-member" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800">
          <Plus size={16} /> Add Member
        </button>
      </div>

      {/* Pending approvals — visible only to founding admin */}
      {isFounding && pending.length > 0 && (
        <div className="mb-6 border-2 border-[#F79009] bg-[#FFFAEB]" data-testid="pending-approvals-section">
          <div className="px-5 py-3 border-b border-[#F79009] flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#B54708]" />
            <h3 className="font-display font-bold text-base tracking-tight">
              {pending.length} payment{pending.length !== 1 ? "s" : ""} received — awaiting your approval
            </h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#F79009]/40 bg-[#FFFAEB]">
                <th className="text-left px-5 py-2 label-caps text-[10px]">User</th>
                <th className="text-left px-5 py-2 label-caps text-[10px]">Email</th>
                <th className="text-left px-5 py-2 label-caps text-[10px]">Plan</th>
                <th className="text-left px-5 py-2 label-caps text-[10px]">Paid until</th>
                <th className="text-right px-5 py-2 label-caps text-[10px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((u) => (
                <tr key={u.id} className="border-b border-[#F79009]/20" data-testid={`pending-${u.id}`}>
                  <td className="px-5 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-[#B54708] text-white flex items-center justify-center font-bold text-xs">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-sm">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-2 text-sm font-mono">{u.email}</td>
                  <td className="px-5 py-2">
                    <span className="text-xs bg-zinc-900 text-white px-2 py-0.5 uppercase">
                      {u.subscription_plan}
                    </span>
                  </td>
                  <td className="px-5 py-2 text-xs text-zinc-700">
                    {u.subscription_status?.current_period_end
                      ? new Date(u.subscription_status.current_period_end).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                      : "—"}
                  </td>
                  <td className="px-5 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => approve(u.id, true)}
                      data-testid={`approve-admin-${u.id}`}
                      className="bg-[#039855] text-white px-3 py-1.5 text-xs font-semibold hover:bg-green-700 mr-1 inline-flex items-center gap-1"
                    >
                      <BadgeCheck size={12} /> Approve + Grant Admin
                    </button>
                    <button
                      onClick={() => approve(u.id, false)}
                      data-testid={`approve-only-${u.id}`}
                      className="border border-zinc-300 px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50"
                    >
                      Approve only
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {Object.entries(roleDescriptions).map(([role, desc]) => (
          <div key={role} className="bg-white border border-zinc-200 p-4">
            <span className={`inline-block px-2.5 py-0.5 text-xs font-medium border uppercase tracking-wider ${roleStyles[role]}`}>{role}</span>
            <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-6 py-3 label-caps">Name</th>
              <th className="text-left px-6 py-3 label-caps">Email</th>
              <th className="text-left px-6 py-3 label-caps">Role</th>
              <th className="text-center px-6 py-3 label-caps">Permissions</th>
              <th className="text-right px-6 py-3 label-caps"></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const userPerms = u.permissions || defaultPerms(u.role);
              const enabledCount = PERM_LABELS.filter(p => userPerms[p.key]).length;
              return (
                <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center font-bold text-sm">{u.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <div className="font-semibold text-sm">{u.name}</div>
                        {u.id === current.id && <div className="text-xs text-zinc-500">You</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm font-mono">{u.email}</td>
                  <td className="px-6 py-3"><span className={`inline-block px-2.5 py-0.5 text-xs font-medium border uppercase tracking-wider ${roleStyles[u.role]}`}>{u.role}</span></td>
                  <td className="px-6 py-3 text-center">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-zinc-300 bg-white text-xs font-mono">
                      <Check size={12} className="text-[#039855]" /> {enabledCount} / {PERM_LABELS.length}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-zinc-100 mr-1" data-testid={`edit-member-${u.id}`}><Pencil size={14} /></button>
                    {u.id !== current.id && <button onClick={() => remove(u.id)} className="p-1.5 hover:bg-red-50 text-[#D92D20]"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Member" : "Add Member"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">Name *</label>
                  <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
                </div>
                <div>
                  <label className="label-caps block mb-2">Role *</label>
                  <select value={form.role} onChange={e => onRoleChange(e.target.value)} data-testid="member-role" className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label-caps block mb-2">Email *</label>
                <input required type="email" disabled={!!editing} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900 disabled:bg-zinc-50 font-mono text-sm" />
              </div>
              <div>
                <label className="label-caps block mb-2">{editing ? "New Password (optional)" : "Password *"}</label>
                <input type="password" required={!editing} minLength={editing ? 0 : 6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
              </div>

              {/* Permissions */}
              <div className="bg-zinc-50 border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="label-caps">Permissions</p>
                    <p className="text-xs text-zinc-600 mt-1">Fine-tune what this member can do. Changing role resets to that role's defaults.</p>
                  </div>
                  <button type="button" onClick={() => setPerms(defaultPerms(form.role))} className="text-xs font-semibold border border-zinc-300 bg-white px-2 py-1 hover:bg-zinc-100">
                    Reset to {form.role} defaults
                  </button>
                </div>
                <div className="space-y-4">
                  {PERM_GROUPS.map(g => (
                    <div key={g.title}>
                      <p className="label-caps text-[10px] mb-2">{g.title}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {g.keys.map(p => (
                          <label key={p.key} className="flex items-center gap-2 cursor-pointer text-sm bg-white border border-zinc-200 px-3 py-2 hover:border-zinc-400" data-testid={`perm-${p.key}`}>
                            <input
                              type="checkbox"
                              checked={!!perms[p.key]}
                              onChange={e => setPerms({ ...perms, [p.key]: e.target.checked })}
                              className="w-4 h-4 accent-zinc-900"
                            />
                            <span>{p.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-member" className="bg-zinc-900 text-white px-4 py-2 font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
